// schedule — the week grid, the month grid, the agenda, and the patch a move
// sends.
//
// Reads GET /api/jobs/schedule?start=&end=, which answers
// { scheduled, backlog, completed } where every row is a jobs row plus
// user_name and subtask counts (server/routes/jobs.js).
//
// ── Why this does not reuse dispatch/lib/format.scheduleChanges ─────────────
//
// It looks like the same function and is not. Dispatch reads
// GET /api/dispatch/board, whose rows name the lead `lead_user_id`; this
// endpoint returns a plain jobs row, where it is `user_id`. One helper
// straddling both would read the wrong field for one of them and silently
// send no change — the sort of thing that looks like "the app forgot my
// edit". Two endpoints, two shapes, two helpers, each named for its own.

import { toISO, atNoon, addDaysISO, todayISO, jobOnDay, dayLabel } from '../../utils/scheduleDates';
// One definition of what "Today" is called and how a date reads — see the note
// on `agendaRows` below.
import { dayHeading } from '../../utils/agenda';
// The board's worker and type filters ask the same questions Office Jobs
// asks, so they are answered in one place — see the filters section below.
import { matchesType, matchesWorker } from './jobFields';

export { toISO, addDaysISO, todayISO, jobOnDay, dayLabel, dayHeading };

/**
 * The start of the week containing `iso`.
 *
 * `startOfWeek` matches the company setting of the same name (0 = Sunday,
 * 1 = Monday). Payroll weeks are computed from it server-side, so a calendar
 * that disagreed would show a week boundary in a different place from the
 * timesheet for the same days.
 */
export function weekStart(iso, startOfWeek = 1) {
  const d = atNoon(iso);
  const dow = d.getDay();                         // 0 Sun … 6 Sat
  const back = (dow - Number(startOfWeek) + 7) % 7;
  return addDaysISO(iso, -back);
}

/** `n` consecutive ISO dates from `startIso`. */
export function daysFrom(startIso, n = 7) {
  return Array.from({ length: n }, (_, i) => addDaysISO(startIso, i));
}

/**
 * Jobs bucketed by day. A job spanning several days appears under EVERY day it
 * covers (jobOnDay), because a three-day build is on the list on all three
 * mornings — not only the first.
 */
export function groupByDay(jobs, days) {
  const out = new Map(days.map((d) => [d, []]));
  for (const job of jobs || []) {
    for (const d of days) if (jobOnDay(job, d)) out.get(d).push(job);
  }
  return out;
}

/**
 * The body for PATCH /api/jobs/:id/schedule — ONLY what changed.
 *
 * The server treats an omitted field as "leave it" and writes an activity
 * line for each field it does change, so sending the whole form would stamp
 * a history entry for every field on every move.
 *
 * `blocked_reason` is deliberately three-state: absent means leave the block
 * alone, a string sets it, null clears it. An empty string means the person
 * cleared the box, which is an unblock, not "no change".
 */
export function schedulePatch(job, changes) {
  const out = {};
  if (!job) return out;

  if ('scheduled_date' in changes) {
    const next = changes.scheduled_date || null;
    if (next !== (job.scheduled_date || null)) out.scheduled_date = next;
  }
  if ('scheduled_end_date' in changes) {
    const next = changes.scheduled_end_date || null;
    if (next !== (job.scheduled_end_date || null)) out.scheduled_end_date = next;
  }
  if ('user_id' in changes) {
    const raw = changes.user_id;
    const next = raw === '' || raw === null || raw === undefined ? null : Number(raw);
    const now = job.user_id === null || job.user_id === undefined ? null : Number(job.user_id);
    if (next !== now) out.user_id = next;
  }
  if ('status' in changes && changes.status && changes.status !== job.status) {
    out.status = changes.status;
  }
  if ('blocked_reason' in changes) {
    const next = (changes.blocked_reason || '').trim() || null;
    if (next !== (job.blocked_reason || null)) out.blocked_reason = next;
  }
  return out;
}

/** Is this job blocked? A non-null reason, not a status (scheduling.js). */
export function isBlocked(job) {
  return !!(job && job.blocked_reason);
}

/** A short label for the week being viewed: "1 – 7 Sep". */
export function weekLabel(startIso, days = 7) {
  return rangeLabel(startIso, addDaysISO(startIso, days - 1));
}

// ── Month and agenda ────────────────────────────────────────────────────────
//
// docs/REBUILD_PARITY.md listed "Month and agenda views" as the one Schedule
// capability with no rebuild home at all. Both are day-bucketing over the SAME
// request the week grid already makes — GET /api/jobs/schedule?start=&end=,
// whose `scheduled` rows are plain jobs rows (`user_id` for the lead, see the
// note at the top of this file) — so the only new logic is which days a view
// covers and how they are chunked. It lives here, with no React around it,
// because every decision below fails silently when it is wrong: a job in the
// wrong cell, a job dropped, or a month grid that quietly renders a spare week.

/** The 1st of the month containing `iso`. Pure string work. */
export function monthStartISO(iso) {
  return `${String(iso).slice(0, 7)}-01`;
}

/**
 * The last day of the month containing `iso`.
 *
 * Day 0 of the NEXT month is the last day of this one, which is the only way
 * to get February right without a leap-year rule of our own. `m` is read
 * 1-based off the string and handed to the Date constructor as a 0-based month
 * index, so it already names the following month.
 */
export function monthEndISO(iso) {
  const [y, m] = String(iso).slice(0, 7).split('-').map(Number);
  return toISO(new Date(y, m, 0, 12, 0, 0));
}

/** Every ISO date from `startIso` to `endIso`, inclusive. */
export function daysBetween(startIso, endIso) {
  const out = [];
  if (!startIso || !endIso || startIso > endIso) return out;
  for (let d = startIso; d <= endIso; d = addDaysISO(d, 1)) out.push(d);
  return out;
}

/**
 * The fetch window for a month grid: whole weeks, so the calendar has no
 * ragged first or last row.
 *
 * `startOfWeek` is the company setting, same as `weekStart` above and for the
 * same reason — payroll weeks are computed from it server-side, so a grid that
 * drew its rows from a different Monday would put a job in a different week
 * from the timesheet covering it. The classic MonthView is hardcoded
 * Monday-first (`monthGridDays`, utils/scheduleDates.js) and this deliberately
 * is not.
 *
 * The loop is capped at six because six whole weeks is the most any month can
 * span (a 31-day month starting on the last day of a week). The cap is not
 * what makes it terminate — `end < last` does — it is there so a malformed
 * anchor cannot spin.
 */
export function monthRange(iso, startOfWeek = 1) {
  const first = monthStartISO(iso);
  const last = monthEndISO(iso);
  const start = weekStart(first, startOfWeek);
  let end = addDaysISO(start, 6);
  for (let i = 0; i < 6 && end < last; i++) end = addDaysISO(end, 7);
  return { start, end, monthStart: first, monthEnd: last };
}

/**
 * The month calendar: `weeks` of seven `{ iso, n, inMonth }`.
 *
 * `inMonth` is what the screen must render differently. A whole-weeks grid
 * always carries days either side of the month, and a leading cell that looks
 * like the others is a day somebody schedules work on believing it is this
 * month.
 */
export function monthGrid(iso, startOfWeek = 1) {
  const range = monthRange(iso, startOfWeek);
  const days = daysBetween(range.start, range.end).map((d) => ({
    iso: d,
    n: Number(d.slice(8, 10)),
    inMonth: d >= range.monthStart && d <= range.monthEnd,
  }));
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return { ...range, days, weeks };
}

/** "September 2026", read off the month the anchor falls in. */
export function monthLabel(iso) {
  return atNoon(monthStartISO(iso)).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/** "31 Aug – 6 Sep", collapsing the month name when it does not change. */
export function rangeLabel(startIso, endIso) {
  const a = atNoon(startIso);
  const b = atNoon(endIso);
  const month = (d) => d.toLocaleDateString(undefined, { month: 'short' });
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()} – ${b.getDate()} ${month(b)}`
    : `${a.getDate()} ${month(a)} – ${b.getDate()} ${month(b)}`;
}

/**
 * The agenda: one entry per day, in date order, for every day that has work.
 *
 * A day with nothing on it is skipped — a month of headings for four days of
 * work is the noise this view exists to remove — with one exception: `today`
 * is kept when it falls inside `days`, so "you have nothing on today" is
 * STATED rather than left to be inferred from an absence. Same decision, and
 * the same reason, as utils/agenda.js `buildAgenda`.
 *
 * `dayHeading` is imported from that module rather than rewritten here so
 * there is ONE rule for what "Today" means and how a date reads. #146 is the
 * precedent: four copies of the day primitives had drifted, and a copy that
 * drifts does not throw, it just puts a job on a different day in one view.
 *
 * Order within a day is the order the rows arrived in, which is the server's
 * `ORDER BY scheduled_date, sort_order, id` (server/routes/jobs.js). The
 * agenda deliberately does not hold a second opinion about sequence.
 *
 * A multi-day job appears under EVERY day it covers, via `jobOnDay` — the same
 * rule `groupByDay` uses above, so the two cannot disagree about which
 * mornings a three-day build is on.
 */
export function agendaRows(jobs, days, today) {
  const list = Array.isArray(jobs) ? jobs.filter(Boolean) : [];
  const out = [];
  for (const iso of days || []) {
    const dayJobs = list.filter((j) => jobOnDay(j, iso));
    if (!dayJobs.length && iso !== today) continue;
    out.push({ ...dayHeading(iso, today), jobs: dayJobs });
  }
  return out;
}

/**
 * How many DISTINCT jobs a bucketing covers — a `groupByDay` Map or a list of
 * `agendaRows`.
 *
 * De-duped by id, because a multi-day job is present in every day it spans:
 * counting the cells would report one three-day build as three jobs. The
 * classic agenda carries the same note about its unassigned count.
 *
 * This is what decides whether a view is allowed to say "nothing is
 * scheduled", and that sentence must never be printed over a refusal — the
 * screen renders the server's own words instead and never reaches here.
 */
export function distinctJobCount(buckets) {
  const ids = new Set();
  const add = (list) => { for (const j of list || []) if (j) ids.add(j.id); };
  if (buckets instanceof Map) buckets.forEach(add);
  else for (const row of buckets || []) add(row && row.jobs);
  return ids.size;
}

// ── The board's filters ─────────────────────────────────────────────────────
//
// docs/PARITY_2026-09-08.md §12: the classic Schedule could be narrowed to one
// worker or one job type, and could choose how far back finished work reached.
// The rebuilt screen shipped with no filters at all and never sent the third
// parameter the endpoint reads.
//
// The two client-side ones are `matchesWorker` and `matchesType` from
// jobFields.js — Office Jobs asks the same questions and must not answer them
// differently. This module holds the window, because that one is a request.


/**
 * The values `GET /api/jobs/schedule?completed=` accepts, and what each one
 * MEANS — read off server/routes/jobs.js:396-410, not invented here:
 *
 *   recent (default)  the 60 most recently finished jobs, whatever the dates
 *                     on screen. This is what the screen has always got,
 *                     because it never sent the parameter.
 *   week              finished INSIDE the requested range (the week, or the
 *                     month grid — this screen sends both)
 *   month             finished in the 30 days up to the end of the range
 *   all               every completion, capped at 500
 *
 * A value the server does not know falls back to `recent` there, so an
 * unrecognised one is normalised here rather than sent: a filter whose answer
 * does not match its label is the control this whole document is about.
 */
export const COMPLETED_WINDOWS = ['recent', 'week', 'month', 'all'];

export const COMPLETED_LABELS = {
  recent: '60 most recent',
  week: 'In the dates shown',
  month: 'Finished in 30 days',
  all: 'Everything',
};

export function normalizeCompleted(value) {
  return COMPLETED_WINDOWS.includes(value) ? value : 'recent';
}

/** Rows the worker and type filters let through. Both empty means every row. */
export function filterBoardJobs(jobs, { type = '', worker = '' } = {}) {
  if (!type && !worker) return jobs || [];
  return (jobs || []).filter((j) => matchesType(j, type) && matchesWorker(j, worker));
}

/**
 * Is anything HIDING work right now?
 *
 * The completed window is deliberately not part of this. It widens or narrows
 * what the server was asked for; it never hides a row the answer contained,
 * and an empty calendar under it is a true statement about the dates on
 * screen. Counting it here would put "these filters hide everything" over a
 * week that is genuinely empty.
 */
export function boardFiltersActive({ type = '', worker = '' } = {}) {
  return !!(type || worker);
}
